/**
 * ELIA Brand — Logo & Wordmark components (source of truth for the entire app)
 *
 * NEVER inline a custom "e" square or the string "ELIA" as a logo in any page.
 * Always import from this file.
 *
 * Variants:
 *   <ElaLogo variant="icon" />          → symbol only (safe zone 6px)
 *   <ElaLogo variant="horizontal" />    → default lockup (symbol + wordmark)
 *   <ElaLogo variant="vertical" />      → stacked (splash / watermarks)
 *   <ElaLogo variant="mono" />          → single-color for print / signature
 *   <ElaLogo variant="dark" />          → for dark backgrounds
 *
 * All variants accept `className` (Tailwind height utilities) + `title`.
 */
import React from "react";

const SYMBOL_PATH = "M14 4 L50 4 C55.523 4 60 8.477 60 14 L60 43 L46 60 L14 60 C8.477 60 4 55.523 4 50 L4 14 C4 8.477 8.477 4 14 4 Z";
const FOLD_PATH   = "M46 60 L60 43 L46 43 Z";
const E_PATH      = "M32 16 C22.611 16 15 23.611 15 33 C15 42.389 22.611 50 32 50 C37.523 50 42.5 47.406 45.6 43.4 C46.4 42.4 45.9 40.9 44.6 40.5 L41 39.4 C40 39.1 39 39.5 38.4 40.3 C36.9 42.3 34.5 43.6 32 43.6 C28.3 43.6 25.1 41.1 24 37.6 L44 37.6 C45.104 37.6 46 36.704 46 35.6 L46 33 C46 23.611 41.389 16 32 16 Z M32 22.4 C36.4 22.4 39 24.9 39 29 L39 31 L24 31 L24 29 C24 24.9 26.8 22.4 32 22.4 Z";


function IconSymbol({ colorMode = "brand" }) {
  const [primary, dark, counter, foldOpacity] =
    colorMode === "dark"     ? ["#5A9974", "#3B7856", "#F8F5EF", 0.35]
    : colorMode === "mono"   ? ["#000000", "#000000", "#FFFFFF", 0.00]
    : colorMode === "white"  ? ["#FFFFFF", "#FFFFFF", "#38724F", 0.20]
    :                          ["url(#eliaBody)", "#255036", "#F8F5EF", 0.14];

  return (
    <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" aria-hidden focusable="false" className="block">
      <defs>
        <linearGradient id="eliaBody" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#38724F"/>
          <stop offset="1" stopColor="#255036"/>
        </linearGradient>
      </defs>
      <path d={SYMBOL_PATH} fill={primary}/>
      {foldOpacity > 0 && <path d={FOLD_PATH} fill={counter} opacity={foldOpacity}/>}
      <path d={E_PATH} fill={counter}/>
    </svg>
  );
}


export function ElaMark({ className = "h-8 w-8", colorMode = "brand", title = "ELIA" }) {
  return (
    <span className={className} role="img" aria-label={title} data-testid="elia-mark">
      <IconSymbol colorMode={colorMode} />
    </span>
  );
}


export function ElaWordmark({ className = "", colorMode = "brand" }) {
  const color = colorMode === "dark" ? "text-background"
              : colorMode === "mono" ? "text-black"
              : colorMode === "white"? "text-white"
              :                        "text-ink";
  return (
    <span className={`font-display font-extrabold tracking-[-0.04em] leading-none ${color} ${className}`}>
      ELIA
    </span>
  );
}


/**
 * <ElaLogo /> — the ONLY logo component to use throughout the app.
 * variant: "horizontal" | "vertical" | "icon" | "mono" | "dark"
 * className: sets height (e.g. "h-8", "h-10"). Width adapts automatically.
 */
export default function ElaLogo({
  variant = "horizontal",
  colorMode,
  className = "h-8",
  title = "ELIA",
  ...rest
}) {
  const mode = colorMode || (variant === "dark" ? "dark" : variant === "mono" ? "mono" : "brand");

  if (variant === "icon") {
    return <ElaMark className={`${className} aspect-square`} colorMode={mode} title={title} />;
  }

  if (variant === "vertical") {
    return (
      <span className={`inline-flex flex-col items-center gap-2 ${className}`}
            role="img" aria-label={title} data-testid="elia-logo-vertical" {...rest}>
        <ElaMark className="h-14 w-14" colorMode={mode} />
        <ElaWordmark className="text-2xl" colorMode={mode} />
      </span>
    );
  }

  // horizontal (default)
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}
          role="img" aria-label={title} data-testid="elia-logo" {...rest}>
      <ElaMark className="h-full aspect-square" colorMode={mode} />
      <ElaWordmark className="text-[1.35em]" colorMode={mode} />
    </span>
  );
}
